/*
 * The pane divider (REUSE J2). Two things about it are not optional: it is operable from the
 * keyboard as well as from a pointer — a separator that only answers a drag fails 2.1.1 — and it
 * announces where it is through `aria-valuenow`, so a person who cannot see the pane still knows
 * how wide it is.
 *
 * Adapted from the product's percentage-with-a-pixel-floor version to plain pixels: the widths this
 * layout cares about are pixel ranges ("the member list stops working below 300 px", spec 12 §4.2),
 * and a percentage of an unknown window is exactly the thing that cannot express them.
 */

import type { KeyboardEvent as ReactKeyboardEvent } from "react"
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type PointerEvent as ReactPointerEvent,
} from "react"
import { writeJSON } from "../../lib/storage.ts"
import { storedWidth } from "./layout.ts"
import "./shell.css"

/** One arrow press, and one Page press, in pixels. */
const STEP_PX = 16
const JUMP_PX = 64

/**
 * How long a pointer has to rest on a handle before the handle answers it (UX §3). Crossing a
 * divider on the way from the sidebar to the message list is not an intention to resize anything,
 * and until this has passed nothing about the handle changes — no cursor, no bar, no paint.
 */
const ARM_AFTER_MS = 300

export interface ResizablePanelOptions {
	/** The full `tb:paneWidths:<pane>` key this width is remembered under. */
	storageKey: string
	defaultWidth: number
	minWidth: number
	maxWidth: number
	/** "start" for a pane left of its handle, "end" for one right of it. */
	side: "start" | "end"
	/** Already translated; the accessible name of the separator. */
	label: string
}

export interface ResizeHandleProps {
	role: "separator"
	tabIndex: 0
	"aria-orientation": "vertical"
	"aria-label": string
	"aria-valuenow": number
	"aria-valuemin": number
	"aria-valuemax": number
	className: string
	/** Set once the handle has been dwelt on or pressed; the only thing the paint reacts to. */
	"data-armed"?: "true" | undefined
	/** Set while a drag is running, which is also what suppresses the text selection under it. */
	"data-dragging"?: "true" | undefined
	onPointerEnter: (event: ReactPointerEvent<HTMLDivElement>) => void
	onPointerLeave: (event: ReactPointerEvent<HTMLDivElement>) => void
	onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void
	onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void
	onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void
	onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void
	onLostPointerCapture: (event: ReactPointerEvent<HTMLDivElement>) => void
	onDoubleClick: () => void
	onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void
}

export interface ResizablePanelResult {
	/** The pane's width in pixels — the one value the caller renders. */
	width: number
	handleProps: ResizeHandleProps
}

/**
 * Whether a pointer event happened over the handle itself. A drag is captured, so its `pointerup`
 * is delivered to the handle wherever the pointer really is, and only the geometry says which.
 */
function overHandle(event: ReactPointerEvent<HTMLDivElement>): boolean {
	const box = event.currentTarget.getBoundingClientRect()
	if (box.width === 0 || box.height === 0) return false
	return (
		event.clientX >= box.left &&
		event.clientX <= box.right &&
		event.clientY >= box.top &&
		event.clientY <= box.bottom
	)
}

export function useResizablePanel(options: ResizablePanelOptions): ResizablePanelResult {
	const { storageKey: key, defaultWidth, minWidth, maxWidth, side, label } = options

	const clamp = useCallback(
		(value: number) => Math.min(maxWidth, Math.max(minWidth, Math.round(value))),
		[minWidth, maxWidth],
	)

	const [width, setWidth] = useState(() =>
		storedWidth(key, { min: minWidth, max: maxWidth, default: defaultWidth }),
	)
	// The drag start is remembered rather than measured per move: reading layout on every pointer
	// move is what makes a resize stutter.
	const drag = useRef({ pointerId: -1, startX: 0, startWidth: 0 })

	const [armed, setArmed] = useState(false)
	const [dragging, setDragging] = useState(false)
	const dwell = useRef<ReturnType<typeof setTimeout> | null>(null)

	const disarm = useCallback(() => {
		if (dwell.current !== null) clearTimeout(dwell.current)
		dwell.current = null
		setArmed(false)
	}, [])

	// A handle that is unmounted mid-dwell must not arm itself afterwards.
	useEffect(() => disarm, [disarm])

	const commit = useCallback(
		(next: number) => {
			const value = clamp(next)
			setWidth(value)
			writeJSON(key, value)
		},
		[clamp, key],
	)

	const handleProps: ResizeHandleProps = {
		role: "separator",
		tabIndex: 0,
		"aria-orientation": "vertical",
		"aria-label": label,
		"aria-valuenow": width,
		"aria-valuemin": minWidth,
		"aria-valuemax": maxWidth,
		className: "tb-resize-handle",
		"data-armed": armed || dragging ? "true" : undefined,
		"data-dragging": dragging ? "true" : undefined,
		onPointerEnter() {
			if (dwell.current !== null) clearTimeout(dwell.current)
			dwell.current = setTimeout(() => {
				dwell.current = null
				setArmed(true)
			}, ARM_AFTER_MS)
		},
		onPointerLeave() {
			if (!dragging) disarm()
		},
		onPointerDown(event) {
			// A decisive grab never waits: a press is the intention the dwell was waiting for.
			disarm()
			setArmed(true)
			setDragging(true)
			event.currentTarget.setPointerCapture(event.pointerId)
			drag.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: width }
		},
		onPointerMove(event) {
			if (drag.current.pointerId !== event.pointerId) return
			const delta = event.clientX - drag.current.startX
			commit(drag.current.startWidth + (side === "start" ? delta : -delta))
		},
		onPointerUp(event) {
			if (drag.current.pointerId !== event.pointerId) return
			drag.current.pointerId = -1
			setDragging(false)
			// A pointer that let go over the handle is resting on it, which is the state the dwell
			// timer arms (UX §3): staying armed keeps the bar and the `col-resize` cursor under a
			// pointer that has not gone anywhere. A drop somewhere else — the usual end of a drag,
			// since the pointer is captured and may be far away — disarms.
			setArmed(overHandle(event))
		},
		onPointerCancel() {
			drag.current.pointerId = -1
			setDragging(false)
			setArmed(false)
		},
		onLostPointerCapture() {
			// The capture always ends with the drop, one event after `onPointerUp` decided whether
			// the handle stays armed; this only ends the drag, so that decision is not undone.
			drag.current.pointerId = -1
			setDragging(false)
		},
		onDoubleClick() {
			commit(defaultWidth)
		},
		onKeyDown(event) {
			// The arrow that grows the pane is the one pointing away from it, in both layouts.
			const towardsStart = event.key === "ArrowLeft"
			const towardsEnd = event.key === "ArrowRight"
			const sign = side === "start" ? 1 : -1
			if (towardsStart || towardsEnd) {
				event.preventDefault()
				commit(width + sign * (towardsEnd ? STEP_PX : -STEP_PX))
				return
			}
			if (event.key === "PageUp" || event.key === "PageDown") {
				event.preventDefault()
				commit(width + sign * (event.key === "PageUp" ? JUMP_PX : -JUMP_PX))
				return
			}
			if (event.key === "Home") {
				event.preventDefault()
				commit(minWidth)
				return
			}
			if (event.key === "End") {
				event.preventDefault()
				commit(maxWidth)
			}
		},
	}

	return { width, handleProps }
}

/** The divider itself; every accessible property it carries comes from the hook. */
export function ResizeHandle(props: ResizeHandleProps) {
	return <div {...props} />
}
